/**
 * the schema plugin module
 */

import _ from 'underscore';

import { get } from './lodash';
import { Registry } from './registry';
import {
  defineConstProperty,
  hasOwnProperty,
} from './reflection';
import typesPlugin from './types-plugin';
import type {
  EdmCallableType,
  EdmOptions,
  EdmType,
  EntityDataModel,
  ODataCallableMap,
  ODataSchema,
  ODataTypeInfo,
  PropertyInfo,
  TypeName,
  TypeRegistry,
} from '../types';

function getSchemaName(schema: ODataSchema) {
  const { $$ODataExtension, name } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.Name;
  }

  // deprecated
  return name;
}

function getSchemaBaseTypeName(schema: ODataSchema) {
  const { $$ODataExtension, clrTypeBase } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.BaseType && $$ODataExtension.BaseType.$ref;
  }

  // deprecated
  return clrTypeBase;
}

function getSchemaKey(schema: ODataSchema) {
  const { $$ODataExtension, key } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.Key;
  }

  // deprecated
  return key;
}

function getSchemaNavigationPropertyNames(schema: ODataSchema) {
  const { $$ODataExtension } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.NavigationProperty;
  }

  return [];
}

export default function schemaPlugin(
  edm: EntityDataModel,
  {
    schemas = {},
    // TODO: deprecated, every schemas should be able to have different namespaces
    // we are still using it with legacy schemas
    namespace = null,
  }: EdmOptions = {},
) {
  if (hasOwnProperty(edm, 'schema')) {
    return;
  }

  typesPlugin(edm);
  const types = edm.types as TypeRegistry;

  const {
    CollectionType,
    EntityType,
    PrimitiveType,
    ComplexType,
    OneOfType,
    ActionType,
    FunctionType,
  } = types;

  defineConstProperty(edm, 'schema', (() => {
    function getSchemaFullName(schema: ODataSchema, path?: string) {
      // $$ref exists in resolved schema
      const possiblePath = (_.isString(path) && path) || schema.$$ref || schema.$ref;
      return possiblePath
        ? possiblePath.replace(/\//g, '.')
        : Registry.getQualifiedName(getSchemaName(schema) as string, namespace);
    }

    function getTypeName(
      dependencies: ODataSchema[],
      typeInfo?: ODataTypeInfo,
    ): TypeName | null {
      let schema: ODataSchema | null = null;
      let typeName: TypeName | null = null;

      if (!typeInfo) {
        return null;
      }

      if (typeInfo.type === 'array') {
        return CollectionType.collectionTypeName(
          getTypeName(dependencies, typeInfo.items),
        );
      }
      if (typeInfo.oneOf) {
        return OneOfType.oneOfTypeName(
          typeInfo.oneOf.map(item => getTypeName(dependencies, item) as TypeName),
        );
      }
      if (typeInfo.type === 'object') {
        ({ schema } = typeInfo);
        // it's possible for the schema to be undefined, use 'object' type by default
        typeName = getSchemaFullName(schema || typeInfo);
      } else if (typeInfo.type === 'string' && typeInfo.enum) {
        // enum type is string, but we need to get a meanful name for different enum type
        // this is to help us disable cache accurately, as enum can be odata call return type now
        typeName = (typeInfo.$$ref || 'string').split('/').join('.');
      } else {
        // primitive types
        typeName = typeInfo.type || null;
      }

      if (schema) {
        dependencies.push(schema);
      }

      return typeName;
    }

    function defineCallableOnType(
      acts: ODataCallableMap | null | undefined,
      funcs: ODataCallableMap | null | undefined,
      type: EdmType,
      qualifiedName: TypeName,
      forEntityType = true,
    ) {
      const actions: EdmCallableType[] = [];
      const functions: EdmCallableType[] = [];

      function getCallableTypeName(key: string) {
        return `${key}##${qualifiedName}${forEntityType ? '' : '@@COLL'}`;
      }

      _.each(_.keys(acts || {}), key => {
        const action = (acts as ODataCallableMap)[key];
        const typeName = getCallableTypeName(key);
        const parameters = _.mapObject(
          action.Parameter || {},
          parameter => ({
            typeName: getTypeName([], parameter),
          }),
        );

        actions.push(new ActionType({
          name: typeName,
          callableName: key,
          parameters,
          returnTypeName: getTypeName([], action.ReturnType),
        }));
      });

      _.each(_.keys(funcs || {}), key => {
        const func = (funcs as ODataCallableMap)[key];
        const typeName = getCallableTypeName(key);
        const parameters = _.mapObject(
          func.Parameter || {},
          parameter => ({
            typeName: getTypeName([], parameter),
          }),
        );

        functions.push(new FunctionType({
          name: typeName,
          callableName: key,
          parameters,
          returnTypeName: getTypeName([], func.ReturnType),
        }));
      });

      defineConstProperty(type, 'callable', {
        actions,
        functions,
      });
    }

    function defineSchemaType(schema: ODataSchema, path?: string) {
      const qualifiedName = getSchemaFullName(schema, path);

      if (!types.resolve(qualifiedName)) {
        const dependencies: ODataSchema[] = [];
        const properties = _.mapObject(
          schema.properties || {},
          (typeInfo): PropertyInfo => ({
            typeName: getTypeName(dependencies, typeInfo),
          }),
        );

        const type = new EntityType({
          name: qualifiedName,
          baseTypeName: getSchemaBaseTypeName(schema) || 'object',
          key: getSchemaKey(schema),
          properties,
          navigationPropertyNames: getSchemaNavigationPropertyNames(schema),
        });

        defineConstProperty(type, 'schema', schema);

        _.each(dependencies, dependency => defineSchemaType(dependency));

        const entityActions = get<ODataCallableMap | null, null>(
          schema,
          '$$ODataExtension.Action',
          null,
        );
        const entityFunctions = get<ODataCallableMap | null, null>(
          schema,
          '$$ODataExtension.Function',
          null,
        );

        if (entityActions || entityFunctions) {
          defineCallableOnType(entityActions, entityFunctions, type, qualifiedName);
        }

        const collCallable = get<{
          Action?: ODataCallableMap;
          Function?: ODataCallableMap;
        } | null, null>(
          schema,
          '$$ODataExtension.Collection',
          null,
        );

        if (collCallable) {
          defineCallableOnType(
            collCallable.Action,
            collCallable.Function,
            type.collectionType,
            qualifiedName,
            false,
          );
        }
      }
    }

    // Define all the primitive types
    _.each({
      string: String,
      integer: Number,
      number: Number,
      datetime: Date,
      boolean: Boolean,
      null: null,
    }, (jsType, name) => new PrimitiveType({ name, jsType }));

    // Define the base object type
    /* eslint no-new: 0 */
    new ComplexType({
      name: 'object',
      properties: {},
    });

    _.each(schemas, (schema: ODataSchema, index: string | number) => {
      if (_.isString(index)) {
        defineSchemaType(schema, index);
      } else {
        defineSchemaType(schema);
      }
    });

    const schemaRegistry = {};
    defineConstProperty(schemaRegistry, 'schemas', schemas);
    defineConstProperty(schemaRegistry, 'namespace', namespace);
    return schemaRegistry;
  })());
}
